/**
 * Soft!=Live Page209 — Settings Integrate residual liquid (Page113/60 + dual).
 * Mount: /settings/integrate
 * Buses (contract ops — never painted in operator DOM).
 * Soft residual deleted:
 *  - invent seat chips while posture loading
 *  - invent Telegram bind when telegram bus sealed
 *  - invent voice template / test-call when voice bus sealed
 *  - path /operation labels painted in DOM
 * Marker: Page209 Soft residual
 */
import { useEffect, useMemo, useState } from "react";
import { html, F, Ico, Card, Field, Input, Alert, KV, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Soft!=Live Page209: missing contract ops must not crash the desk. */
function safeIntegratePath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_integrate_status") return "/settings/integrate/status";
    if (op === "get__admin_telegram_status") return "/admin/telegram/status";
    if (op === "get__admin_telegram_bindings") return "/admin/telegram/bindings";
    if (op === "get__admin_telegram_webhook-info") return "/admin/telegram/webhook-info";
    if (op === "post__telegram_link-token") return "/telegram/link-token";
    if (op === "post__admin_telegram_bind-self") return "/admin/telegram/bind-self";
    if (op === "post__admin_telegram_test") return "/admin/telegram/test";
    if (op === "patch__admin_telegram_bindings_p_enabled") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      const en = opts && opts.query && opts.query.enabled;
      return id
        ? "/admin/telegram/bindings/" + encodeURIComponent(id) + "/enabled?enabled=" + String(en)
        : "";
    }
    if (op === "delete__admin_telegram_bindings_p") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/admin/telegram/bindings/" + encodeURIComponent(id) : "";
    }
    if (op === "get__admin_voice_templates") return "/admin/voice/templates";
    if (op === "put__admin_voice_templates_p") {
      const n = opts && opts.pathParams && opts.pathParams[0];
      return n ? "/admin/voice/templates/" + encodeURIComponent(n) : "";
    }
    if (op === "post__admin_voice_test-call") return "/admin/voice/test-call";
    return "";
  }
}

/** Path-free operator copy for Integrations desk. */
export function softIntegrateSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|admin required/.test(low)) {
    return "Your account cannot change integrations here. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|chat|template|username/.test(low)) {
    return "That integration change was not accepted. Check the fields and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries - wait a moment, then retry.";
  }
  if (/\b404\b|not found|disabled|not.?armed|unavailable|\b503\b/.test(low)) {
    return "This channel is not available on the deployment yet.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue - try again in a moment.";
  }
  if (/\/api\/|\/admin\/|\/telegram|\/voice|\/settings\/integrate|GET |POST |PATCH |DELETE |PUT |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "...";
  return n;
}

/** Pure: seat chips from Live Settings Integrate posture (Page209). */
export function integrateSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const bMax = Number(lim.binding_paint_max);
  const tMax = Number(lim.template_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    telegramAllowed: p.telegram_channel_allowed === true,
    telegramWriteAllowed: p.telegram_write_allowed === true,
    telegramAdminStatus: p.telegram_admin_status_allowed === true,
    voiceAllowed: p.voice_channel_allowed === true,
    voiceWriteAllowed: p.voice_write_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim() || null,
    softTitle: String(p.soft_title || "").trim() || null,
    softMessage: String(p.soft_message || "").trim() || null,
    forbidApiPaint: p.soft_api_path_paint_forbidden !== false,
    bindingPaintMax:
      Number.isFinite(bMax) && bMax > 0 ? Math.floor(bMax) : 40,
    templatePaintMax:
      Number.isFinite(tMax) && tMax > 0 ? Math.floor(tMax) : 24,
  };
}

/** Pure: webhook URL only — getWebhookInfo ok/live is not a set proof. */
export function telegramWebhookUrlOf(webhook) {
  const wh = webhook && typeof webhook === "object" ? webhook : {};
  const pack = (wh.webhook && typeof wh.webhook === "object") ? wh.webhook : {};
  return String(pack.url || wh.url || "").trim();
}

/** Pure: KPI strip from Live telegram + voice packs. */
export function integrateKpisOf(tgStatus, bindings, webhook, voiceTpl, opts) {
  const st = tgStatus && typeof tgStatus === "object" ? tgStatus : {};
  const items = (bindings && (bindings.items || bindings.bindings)) || [];
  const tpls = (voiceTpl && (voiceTpl.templates || voiceTpl.items)) || [];
  const o = opts && typeof opts === "object" ? opts : {};
  return {
    tgOn: !!(st.enabled || st.configured || st.ok || st.chats),
    binds: Array.isArray(items) ? items.length : 0,
    hookLive: !!telegramWebhookUrlOf(webhook),
    voiceTpls: Array.isArray(tpls) ? tpls.length : 0,
    voiceFlag: !!o.voiceFlag,
  };
}

export function IntegrateSettingsDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  /* Session honesty only — seat planes still gate Live buses. */
  const voiceFlag = !!(me && me.features && me.features.voice);
  const billingFlag = !!(me && me.features && me.features.billing);

  const posture = useApi(safeIntegratePath("get__settings_integrate_status"));
  const post = posture.data || {};
  // Fail-closed while loading - seats fail-closed (ignore client feature invent).
  const seat = integrateSettingsSeatOf(posture.loading || !posture.data ? {} : post);

  // Live seat from status - not Soft me.features.voice wall.
  const tgSeatOpen = seat.telegramAllowed;
  const tgAdminOpen = seat.telegramAdminStatus;
  const voiceSeatOpen = seat.voiceAllowed;

  const tgStatus = useApi(tgAdminOpen ? safeIntegratePath("get__admin_telegram_status") : null);
  const bindings = useApi(tgSeatOpen ? safeIntegratePath("get__admin_telegram_bindings") : null);
  const webhook = useApi(tgAdminOpen ? safeIntegratePath("get__admin_telegram_webhook-info") : null);
  const voiceTpl = useApi(voiceSeatOpen ? safeIntegratePath("get__admin_voice_templates") : null);
  const act = useAction();
  const envelope = act.envelope;

  const [chatId, setChatId] = useState("");
  const [link, setLink] = useState(null);
  const [voiceUser, setVoiceUser] = useState("");
  const [tplName, setTplName] = useState("default");
  const [tplText, setTplText] = useState("");
  const [tplLang, setTplLang] = useState("zh-CN");

  const items = useMemo(() => {
    const d = bindings.data;
    const rows = (d && (d.items || d.bindings)) || [];
    return rows.slice(0, seat.bindingPaintMax);
  }, [bindings.data, seat.bindingPaintMax]);

  const templates = useMemo(() => {
    const d = voiceTpl.data;
    const rows = (d && (d.templates || d.items)) || [];
    return rows.slice(0, seat.templatePaintMax);
  }, [voiceTpl.data, seat.templatePaintMax]);

  useEffect(() => {
    if (!templates.length) return;
    const hit = templates.find((t) => String(t.name || "") === tplName) || templates[0];
    if (!hit) return;
    setTplName(String(hit.name || "default"));
    setTplText(String(hit.text || hit.body || ""));
    setTplLang(String(hit.language || hit.lang || "zh-CN"));
  }, [voiceTpl.data]);

  const kpis = integrateKpisOf(
    tgStatus.data,
    bindings.data,
    webhook.data,
    voiceTpl.data,
    { voiceFlag },
  );

  const tgDenied = !!(tgStatus.error && /\b403\b|forbidden/i.test(tgStatus.error || ""));
  const voiceDenied = !!(voiceTpl.error && /\b403\b|forbidden/i.test(voiceTpl.error || ""));
  const voiceUnarmed = !!(voiceTpl.error && /\b404\b|disabled|not.?armed/i.test(voiceTpl.error || ""));
  const botConfigured = !!(tgStatus.data && (
    tgStatus.data.configured || tgStatus.data.enabled || tgStatus.data.ok
  ));
  const wh = webhook.data || {};

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("You cannot re-read this desk from this account", "warn");
      return;
    }
    posture.reload && posture.reload();
    tgStatus.reload && tgStatus.reload();
    bindings.reload && bindings.reload();
    webhook.reload && webhook.reload();
    voiceTpl.reload && voiceTpl.reload();
    toast && toast("Integrations refreshed", "go");
  };

  const genLink = async () => {
    if (!seat.telegramWriteAllowed) {
      toast("Telegram is sealed for this account", "warn");
      return;
    }
    const out = await act.run(safeIntegratePath("post__telegram_link-token"), {
      method: "POST",
      body: { enabled_on_bind: true },
    });
    if (out) {
      setLink(out);
      toast("Connect link ready", "go");
    } else toast(softIntegrateSettingsErrorOf(act.error) || "Could not mint a connect link", "bad");
  };

  const bindManual = async () => {
    if (!seat.telegramWriteAllowed) {
      toast("Telegram is sealed for this account", "warn");
      return;
    }
    if (!chatId.trim()) {
      toast("Enter a chat number first", "bad");
      return;
    }
    const out = await act.run(safeIntegratePath("post__admin_telegram_bind-self"), {
      method: "POST",
      body: { chat_id: chatId.trim(), enabled: true },
    });
    if (out) {
      toast("Telegram chat linked", "go");
      setChatId("");
      bindings.reload && bindings.reload();
    } else toast(softIntegrateSettingsErrorOf(act.error) || "Could not link that chat", "bad");
  };

  const toggle = async (id, enabled) => {
    if (!seat.telegramWriteAllowed) {
      toast("Telegram is sealed for this account", "warn");
      return;
    }
    const out = await act.run(
      safeIntegratePath("patch__admin_telegram_bindings_p_enabled", {
        pathParams: [id],
        query: { enabled: !enabled },
      }),
      { method: "PATCH" },
    );
    if (out) {
      toast(!enabled ? "Chat enabled" : "Chat paused", "go");
      bindings.reload && bindings.reload();
    } else toast(softIntegrateSettingsErrorOf(act.error) || "Could not update that chat", "bad");
  };

  const unbind = async (id) => {
    if (!seat.telegramWriteAllowed) {
      toast("Telegram is sealed for this account", "warn");
      return;
    }
    if (!window.confirm("Remove Telegram chat " + id + " from this account?")) return;
    const out = await act.run(
      safeIntegratePath("delete__admin_telegram_bindings_p", { pathParams: [id] }),
      { method: "DELETE" },
    );
    if (out) {
      toast("Telegram chat removed", "warn");
      bindings.reload && bindings.reload();
    } else toast(softIntegrateSettingsErrorOf(act.error) || "Could not remove that chat", "bad");
  };

  const testTg = async () => {
    if (!seat.telegramAdminStatus) {
      toast("Channel tests need a wider admin grant", "warn");
      return;
    }
    const out = await act.run(safeIntegratePath("post__admin_telegram_test"), {
      method: "POST",
      body: {},
    });
    if (out && out.ok === true) toast("Test message sent to Telegram", "go");
    else toast(
      softIntegrateSettingsErrorOf(act.error)
        || String((out && (out.detail || out.reason)) || "").slice(0, 96)
        || "Telegram test was refused",
      "bad",
    );
  };

  const testVoice = async () => {
    if (!seat.voiceWriteAllowed) {
      toast("Voice tests are sealed for this account", "warn");
      return;
    }
    if (!voiceUser.trim()) {
      toast("Enter a username for the test call", "bad");
      return;
    }
    const out = await act.run(safeIntegratePath("post__admin_voice_test-call"), {
      method: "POST",
      body: { username: voiceUser.trim(), message: "Sentinel console voice test" },
    });
    if (out) toast("Test call started for " + voiceUser.trim(), "go");
    else toast(softIntegrateSettingsErrorOf(act.error) || "Voice test was refused", "bad");
  };

  const saveVoiceTpl = async () => {
    if (!seat.voiceWriteAllowed || voiceDenied || voiceUnarmed) {
      toast("Voice templates are sealed for this account", "warn");
      return;
    }
    const name = tplName.trim() || "default";
    if (!tplText.trim()) {
      toast("Template text is required - nothing saved", "warn");
      return;
    }
    const out = await act.run(
      safeIntegratePath("put__admin_voice_templates_p", { pathParams: [name] }),
      {
        method: "PUT",
        body: { text: tplText, language: tplLang.trim() || "zh-CN" },
      },
    );
    if (!out) {
      toast(softIntegrateSettingsErrorOf(act.error) || "Could not save the voice template", "bad");
      return;
    }
    toast("Voice template saved - " + name, "go");
    voiceTpl.reload && voiceTpl.reload();
  };

  const softTitle = seat.softTitle || seat.postureLabel || "Outbound integrations";
  const softLede = seat.softMessage || seat.postureLabel || (
    "Connect Telegram and voice so incident alerts can leave the console. "
    + "Each action is checked on the server for your role."
  );

  return html`<div className="int-set-desk int-set-desk--liquid"
    data-cmc-page="page113-integrate" data-cmc-integrate="page60"
    data-cmc-residual="page209-integrate" data-cmc-integrate113="page113-integrate"
    data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
    <div className="int-set-desk__rail">
      <div>
        <p className="eyebrow">Settings · Integrations</p>
        <h2 className="int-set-desk__title">${softTitle}</h2>
        <p className="note">${softLede}</p>
      </div>
      <div className="btn-row int-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${act.busy || !seat.reReadAllowed}
          onClick=${reload}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${()=>go("/settings/notify")}>Notification preferences</button>
      </div>
    </div>

    <${Alert} tone="info"
      title=${seat.telegramAllowed && seat.voiceAllowed
        ? "Telegram and voice are available"
        : seat.telegramAllowed
          ? "Telegram available · voice sealed"
          : seat.voiceAllowed
            ? "Voice available · Telegram sealed"
            : "Outbound channels limited"}
      say=${`Signed in as ${seat.role || role || "operator"}. Server seat still decides every bind, test, and template write.`}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="int-set-desk__kpis" role="group" aria-label="Integration summary">
      <div className="int-set-desk__kpi" data-on=${seat.telegramAllowed && kpis.tgOn ? "1" : "0"}>
        <span>Telegram</span>
        <b>${!seat.telegramAllowed ? "sealed" : (tgDenied ? "restricted" : (botConfigured ? "ready" : "idle"))}</b>
        <em>alerts</em>
      </div>
      <div className="int-set-desk__kpi" data-on=${seat.telegramAllowed && kpis.binds ? "1" : "0"}>
        <span>Linked chats</span>
        <b>${!seat.telegramAllowed || bindings.error ? "—" : kpis.binds}</b>
        <em>telegram</em>
      </div>
      <div className="int-set-desk__kpi" data-on=${seat.telegramAllowed && kpis.hookLive ? "1" : "0"}>
        <span>Bot webhook</span>
        <b>${!seat.telegramAllowed || webhook.error ? "—" : (kpis.hookLive ? "on site" : "standby")}</b>
        <em>delivery</em>
      </div>
      <div className="int-set-desk__kpi" data-on=${seat.voiceAllowed && kpis.voiceTpls ? "1" : "0"}>
        <span>Voice scripts</span>
        <b>${!seat.voiceAllowed ? "sealed" : (voiceDenied || voiceUnarmed ? "—" : kpis.voiceTpls)}</b>
        <em>templates</em>
      </div>
    </div>

    <${Card} title="Channel overview" meta="what this deployment exposes"
        sub="Feature chips are honesty only. Bind and template writes still need a server seat.">
      <div className="lane" data-state="live">
        <span className="lane__st"/><span className="txt">
          <b>Email</b><span>Mail channel for the deployment — secrets stay outside the console.</span>
        </span><span className="chip">available</span>
      </div>
      <div className="lane" data-state="live">
        <span className="lane__st"/><span className="txt">
          <b>Phone push</b><span>Registration tokens follow each sign-in.</span>
        </span><span className="chip">available</span>
      </div>
      <div className="lane" data-state=${seat.telegramAllowed && (botConfigured || items.length) ? "live" : "shadow"}
          style=${seat.telegramAllowed && (botConfigured || items.length) ? null : { opacity: 0.75 }}>
        <span className="lane__st"/><span className="txt">
          <b>Telegram</b><span>Link chats below for outbound alerts.</span>
        </span>
        <span className="chip" data-tone=${seat.telegramAllowed && botConfigured ? "" : "off"}>
          ${!seat.telegramAllowed ? "sealed" : (tgDenied ? "restricted" : (botConfigured ? "ready" : "idle"))}
        </span>
      </div>
      <div className="lane" data-state=${billingFlag ? "live" : "shadow"}
          style=${billingFlag ? null : { opacity: 0.55 }}>
        <span className="lane__st"/><span className="txt">
          <b>Billing</b><span>Invoices only when billing is armed — open the Billing desk.</span>
        </span>
        <span className="chip" data-tone=${billingFlag ? "" : "off"}>
          ${billingFlag ? "available" : "off"}
        </span>
      </div>
      <div className="lane" data-state=${seat.voiceAllowed && !voiceDenied && !voiceUnarmed ? "live" : "shadow"}
          style=${seat.voiceAllowed && !voiceDenied && !voiceUnarmed ? null : { opacity: 0.75 }}>
        <span className="lane__st"/><span className="txt">
          <b>Voice</b><span>Spoken templates for incident calls when the voice seat is open.</span>
        </span>
        <span className="chip" data-tone=${!seat.voiceAllowed || voiceDenied || voiceUnarmed ? "off" : ""}>
          ${!seat.voiceAllowed ? "sealed" : voiceDenied ? "restricted" : voiceUnarmed ? "off" : (kpis.voiceTpls + " scripts")}
        </span>
      </div>
    <//>

    ${!seat.telegramAllowed ? html`
      <${Alert} tone="warn" title="Telegram sealed"
        say="This account cannot mint links, bind chats, or send Telegram tests."/>`
    : html`
    <${Card} title="Telegram" meta="status · webhook · linked chats"
        sub="Generate a one-click link, bind a chat number, or run an admin health test.">
      ${tgDenied ? html`
        <${Alert} tone="info" title="Telegram status is restricted"
          say="Bot health and test sends need a wider grant. You may still manage your own chat links."/>` : null}
      ${tgStatus.data ? html`<div className="grid g2" style=${{ marginBottom: 10 }}>
        <${KV} k="Enabled" v=${String(!!tgStatus.data.enabled)}/>
        <${KV} k="Chats" v=${String(tgStatus.data.chats ?? "—")}/>
        <${KV} k="Worker" v=${tgStatus.data.worker_running ? "running" : "idle"}/>
        <${KV} k="Last send" v=${tgStatus.data.last_send_ok ? "ok" : "—"}/>
      </div>` : null}
      ${webhook.loading ? html`<p className="note">Checking bot delivery…</p>`
        : webhook.error ? html`<p className="note">${softIntegrateSettingsErrorOf(webhook.error) || "Webhook status not available"}</p>`
        : webhook.data ? html`<${Alert}
            tone=${kpis.hookLive ? "info" : "warn"}
            title=${kpis.hookLive ? "Bot webhook is set" : "Bot webhook is on standby"}
            say=${kpis.hookLive
              ? "A webhook URL is registered with Telegram."
              : (wh.reason || wh.tier
                ? "Delivery posture: " + String(wh.reason || wh.tier)
                : "Telegram answered. No webhook URL is set.")}/>` : null}

      <div className="btn-row">
        <button type="button" className="btn btn--go btn--sm"
          disabled=${act.busy || !seat.telegramWriteAllowed}
          onClick=${genLink}>Generate connect link</button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${act.busy || tgDenied || !seat.telegramAdminStatus}
          onClick=${testTg}>Send test message</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${()=>{ bindings.reload && bindings.reload(); toast("Linked chats refreshed", "go"); }}>
          Refresh chats
        </button>
      </div>

      ${link ? html`<p className="note" style=${{ marginTop: 10 }}>
        ${link.deep_link
          ? html`<a href=${link.deep_link} target="_blank" rel="noopener">Open one-click bind</a>
              ${" · "}<a href=${link.open_chat_url || link.deep_link} target="_blank" rel="noopener">Open bot</a>`
          : html`Bot username is not set — start the bot with payload ${link.start_payload || "—"}`}
      </p>` : null}

      ${bindings.loading
        ? html`<p className="note">Loading linked chats…</p>`
        : bindings.error
          ? html`<${Alert} tone="warn" title="Could not list chats"
              say=${softIntegrateSettingsErrorOf(bindings.error) || "Linked chats are not available right now."}/>`
          : html`<div className="rows" style=${{ marginTop: 12 }}>
              ${!items.length ? html`<p className="note">No chats linked yet.</p>` : null}
              ${items.map((it) => html`
                <div key=${it.chat_id} className="row row--say">
                  <${Ico} d="share" w=${14}/>
                  <span className="main">
                    <b>${it.chat_id}</b>
                    <span>${it.enabled ? "enabled" : "paused"} · ${it.updated_at || it.created_at || ""}</span>
                  </span>
                  <button type="button" className="btn btn--text btn--sm"
                    disabled=${act.busy || !seat.telegramWriteAllowed}
                    onClick=${()=>toggle(it.chat_id, !!it.enabled)}>
                    ${it.enabled ? "Pause" : "Enable"}
                  </button>
                  <button type="button" className="btn btn--text btn--sm"
                    disabled=${act.busy || !seat.telegramWriteAllowed}
                    onClick=${()=>unbind(it.chat_id)}>Remove</button>
                </div>`)}
            </div>`}

      <div className="grid g2" style=${{ marginTop: 12 }}>
        <${Field} label="Manual chat number" hint="Fallback if the deep link fails.">
          <${Input} icon="chip" value=${chatId}
            onChange=${(e)=>setChatId(e.target.value)} placeholder="2082431201"/>
        <//>
      </div>
      <button type="button" className="btn btn--ghost btn--sm" style=${{ marginTop: 8 }}
        disabled=${act.busy || !chatId.trim() || !seat.telegramWriteAllowed} onClick=${bindManual}>
        Link chat manually
      </button>
    <//>`}

    ${!seat.voiceAllowed ? html`
      <${Alert} tone="info" title="Voice sealed"
        say="Only organisation admins manage voice scripts and place test calls from this desk."/>`
    : html`
    <${Card} title="Voice scripts" meta="templates · test call"
        sub="Save spoken text templates and place a bounded test call. Server seat is authoritative.">
      ${!voiceFlag ? html`
        <${Alert} tone="info" title="Voice delivery is on standby"
          say="Voice is off for this sign-in. Calls stay unarmed until the deployment turns voice on. Templates still load when the voice seat is open."/>` : null}
      ${voiceDenied ? html`
        <${Alert} tone="info" title="Voice templates restricted"
          say="Admin grant required for templates. This seat cannot invent scripts."/>` : null}
      ${voiceUnarmed ? html`
        <${Alert} tone="warn" title="Voice is not armed"
          say=${softIntegrateSettingsErrorOf(voiceTpl.error) || "Voice templates are not available until the deployment arms voice."}/>` : null}
      ${!voiceDenied && !voiceUnarmed ? html`<${F}>
        ${voiceTpl.loading
          ? html`<p className="note">Loading templates…</p>`
          : voiceTpl.error
            ? html`<${Alert} tone="warn" title="Could not load voice templates"
                say=${softIntegrateSettingsErrorOf(voiceTpl.error) || "Voice templates are not available right now."}/>`
            : html`<${F}>
                <div className="chips" style=${{ marginBottom: 8 }}>
                  ${!templates.length ? html`<span className="chip" data-tone="off">no templates yet</span>` : null}
                  ${templates.map((t) => html`
                    <button key=${t.id || t.key || t.name} type="button" className="chip"
                      data-tone="ai" data-sel=${tplName === (t.name || t.id) ? "1" : "0"}
                      onClick=${() => {
                        setTplName(String(t.name || t.id || "default"));
                        setTplText(String(t.text || t.body || ""));
                        setTplLang(String(t.language || t.lang || "zh-CN"));
                      }}>${t.label || t.name || t.id || t.key}</button>`)}
                </div>
                <${Field} label="Template name">
                  <${Input} icon="book" value=${tplName} onChange=${(e)=>setTplName(e.target.value)}/>
                <//>
                <${Field} label="Spoken text" hint="Placeholders like device names stay resolved on the server.">
                  <textarea className="inp" rows=${3} value=${tplText}
                    onChange=${(e)=>setTplText(e.target.value)}/>
                <//>
                <${Field} label="Language">
                  <${Input} icon="brain" value=${tplLang} onChange=${(e)=>setTplLang(e.target.value)}/>
                <//>
                <button type="button" className="btn btn--go btn--sm"
                  disabled=${act.busy || !seat.voiceWriteAllowed}
                  onClick=${saveVoiceTpl} style=${{ marginTop: 8 }}>
                  Save template
                </button>
              <//>`}
        <${Field} label="Test call username" hint="Places a single voice test for that account.">
          <${Input} icon="user" value=${voiceUser}
            onChange=${(e)=>setVoiceUser(e.target.value)} placeholder="tenant.admin"/>
        <//>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${act.busy || !voiceUser.trim() || !seat.voiceWriteAllowed} onClick=${testVoice}>
          Place test call
        </button>
      <//>` : null}
    <//>`}
  </div>`;
}

export default IntegrateSettingsDesk;
